import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { getInquiryStats } from '../api/educationInquiries';
import {
  INQUIRY_STATUSES,
  INQUIRY_STATUS_MAP,
  STATS_WINDOW_OPTIONS,
} from '../types/educationInquiry';
import type {
  InquiryStats,
  InquiryStatus,
  InquiryStatsTimeSeriesPoint,
} from '../types/educationInquiry';
import styles from './EducationAnalytics.module.css';

const CURRENCY = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
const COMPACT_NUM = new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 });

function fmtPct(value: number | null): string {
  if (value == null) return '—';
  return `${(value * 100).toFixed(1)}%`;
}

function fmtCurrency(value: string | null): string {
  if (value == null) return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return value;
  return CURRENCY.format(n);
}

function fmtDuration(seconds: number | null): string {
  if (seconds == null) return '—';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const minutes = seconds / 60;
  if (minutes < 60) return `${minutes.toFixed(1)} min`;
  const hours = minutes / 60;
  if (hours < 24) return `${hours.toFixed(1)} hr`;
  const days = hours / 24;
  if (days < 14) return `${days.toFixed(1)} days`;
  const weeks = days / 7;
  if (weeks < 8) return `${weeks.toFixed(1)} wks`;
  return `${(days / 30).toFixed(1)} mo`;
}

// Bucket daily points into weekly buckets when window is large
function bucketWeekly(points: InquiryStatsTimeSeriesPoint[]): InquiryStatsTimeSeriesPoint[] {
  if (points.length === 0) return [];
  const buckets: InquiryStatsTimeSeriesPoint[] = [];
  for (let i = 0; i < points.length; i += 7) {
    const slice = points.slice(i, i + 7);
    if (slice.length === 0) continue;
    buckets.push({
      date: slice[0].date,
      count: slice.reduce((s, p) => s + p.count, 0),
      won: slice.reduce((s, p) => s + p.won, 0),
      estimated_units: slice.reduce((s, p) => s + p.estimated_units, 0),
    });
  }
  return buckets;
}

export default function EducationAnalytics() {
  const [windowDays, setWindowDays] = useState<number>(90);
  const [stats, setStats] = useState<InquiryStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastFetch, setLastFetch] = useState<Date | null>(null);

  const loadStats = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await getInquiryStats(windowDays);
      setStats(data);
      setLastFetch(new Date());
    } catch {
      setError('Failed to load analytics');
    } finally {
      setIsLoading(false);
    }
  }, [windowDays]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const series = useMemo(() => {
    if (!stats) return [];
    return windowDays > 60 ? bucketWeekly(stats.time_series_daily) : stats.time_series_daily;
  }, [stats, windowDays]);

  const seriesGranularity = windowDays > 60 ? 'week' : 'day';

  const isEmpty = stats != null && stats.total_inquiries === 0;
  const hasFailedEmails = (stats?.email_delivery.failed ?? 0) > 0;

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>Education Analytics</h1>
          <p className={styles.subtitle}>
            Performance of the education program · time-series window: {windowDays} days
          </p>
        </div>
        <div className={styles.headerActions}>
          {lastFetch && (
            <span className={styles.lastUpdated}>Updated {format(lastFetch, 'h:mm a')}</span>
          )}
          <button className={styles.refreshButton} onClick={loadStats} disabled={isLoading}>
            <span className={`${styles.refreshIcon} ${isLoading ? styles.spinning : ''}`}>↻</span>
            Refresh
          </button>
        </div>
      </div>

      <div className={styles.windowPills}>
        {STATS_WINDOW_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            className={`${styles.pill} ${windowDays === opt.value ? styles.pillActive : ''}`}
            onClick={() => setWindowDays(opt.value)}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {error && (
        <div className={styles.error}>
          {error}
          <button className={styles.retryButton} onClick={loadStats}>
            Retry
          </button>
        </div>
      )}

      {hasFailedEmails && stats && (
        <Link
          to={`/education/inquiries?email_sent=false`}
          className={styles.warningBanner}
        >
          <span>⚠</span>
          <span>
            <strong>{stats.email_delivery.failed}</strong> notification email
            {stats.email_delivery.failed === 1 ? '' : 's'} failed to send. These leads may be sitting in the
            queue without anyone notified — review them now.
          </span>
          <span className={styles.warningCta}>Review →</span>
        </Link>
      )}

      {isLoading && !stats ? (
        <div className={styles.loading}>Loading analytics…</div>
      ) : isEmpty ? (
        <div className={styles.emptyState}>
          <h2>No inquiries yet</h2>
          <p>Submissions from byok.io will appear here. Once a few come in, charts will populate automatically.</p>
        </div>
      ) : stats ? (
        <>
          {/* KPI strip */}
          <div className={styles.kpiGrid}>
            <KpiCard label="Total Inquiries" value={stats.total_inquiries.toLocaleString()} />
            <KpiCard
              label="Last 7 Days"
              value={stats.recent_counts.last_7_days.toLocaleString()}
              sub={`${stats.recent_counts.last_30_days.toLocaleString()} in last 30d`}
            />
            <KpiCard
              label="Unassigned"
              value={stats.unassigned_count.toLocaleString()}
              tone={stats.unassigned_count > 0 ? 'warn' : 'neutral'}
              href={`/education/inquiries?assigned_to=unassigned`}
              hint="Click to triage"
            />
            <KpiCard
              label="Conversion Rate"
              value={fmtPct(stats.conversion.conversion_rate)}
              sub={
                stats.conversion.decided_count > 0
                  ? `${stats.conversion.won_count} won / ${stats.conversion.lost_count} lost`
                  : 'No decisions yet'
              }
              tone="good"
            />
            <KpiCard
              label="Won Units"
              value={stats.quantities.won_units.toLocaleString()}
              sub={`${stats.quantities.total_estimated_units.toLocaleString()} estimated total`}
            />
            <KpiCard
              label="Conversion Value"
              value={fmtCurrency(stats.conversion.total_conversion_value)}
              tone="good"
            />
          </div>

          {/* Time series + status donut */}
          <div className={styles.chartsRow}>
            <section className={styles.chartCard}>
              <div className={styles.chartHeader}>
                <h2 className={styles.chartTitle}>Inquiries over time</h2>
                <span className={styles.chartMeta}>
                  bucketed by {seriesGranularity} · {series.length} {seriesGranularity}
                  {series.length === 1 ? '' : 's'}
                </span>
              </div>
              <TimeSeriesChart points={series} granularity={seriesGranularity} />
              <div className={styles.legend}>
                <LegendDot color="#3b82f6" label="Inquiries" />
                <LegendDot color="#10b981" label="Won" />
              </div>
            </section>

            <section className={styles.chartCard}>
              <div className={styles.chartHeader}>
                <h2 className={styles.chartTitle}>Status distribution</h2>
                <span className={styles.chartMeta}>all-time</span>
              </div>
              <StatusDonut counts={stats.status_counts} total={stats.total_inquiries} />
            </section>
          </div>

          {/* Top breakdowns */}
          <div className={styles.chartsRow}>
            <BarBreakdown
              title="Top intended uses"
              data={stats.top_intended_uses}
              total={stats.total_inquiries}
              barColor="#06b6d4"
            />
            <BarBreakdown
              title="Top timelines"
              data={stats.top_timelines}
              total={stats.total_inquiries}
              barColor="#f59e0b"
            />
          </div>

          {/* Conversion / response time stats */}
          <section className={styles.chartCardWide}>
            <h2 className={styles.chartTitle}>Conversion & response times</h2>
            <div className={styles.statRow}>
              <Stat label="Avg. time to first contact" value={fmtDuration(stats.conversion.avg_time_to_contact_seconds)} />
              <Stat label="Avg. time to conversion" value={fmtDuration(stats.conversion.avg_time_to_conversion_seconds)} />
              <Stat label="Decided leads" value={stats.conversion.decided_count.toLocaleString()} />
              <Stat
                label="Avg. estimated units"
                value={
                  stats.quantities.avg_estimated_units != null
                    ? stats.quantities.avg_estimated_units.toFixed(1)
                    : '—'
                }
              />
              <Stat
                label="Email delivery"
                value={`${(stats.email_delivery.success_rate * 100).toFixed(1)}%`}
                sub={`${stats.email_delivery.sent} sent · ${stats.email_delivery.failed} failed`}
                tone={stats.email_delivery.failed > 0 ? 'warn' : 'good'}
              />
            </div>
          </section>
        </>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// KPI Card
// ---------------------------------------------------------------------------

interface KpiProps {
  label: string;
  value: string;
  sub?: string;
  tone?: 'neutral' | 'good' | 'warn';
  href?: string;
  hint?: string;
}

function KpiCard({ label, value, sub, tone = 'neutral', href, hint }: KpiProps) {
  const cls = `${styles.kpi} ${tone === 'good' ? styles.kpiGood : tone === 'warn' ? styles.kpiWarn : ''}`;
  const inner = (
    <>
      <div className={styles.kpiLabel}>{label}</div>
      <div className={styles.kpiValue}>{value}</div>
      {sub && <div className={styles.kpiSub}>{sub}</div>}
      {href && hint && <div className={styles.kpiHint}>{hint}</div>}
    </>
  );
  if (href) {
    return (
      <Link to={href} className={`${cls} ${styles.kpiLink}`}>
        {inner}
      </Link>
    );
  }
  return <div className={cls}>{inner}</div>;
}

// ---------------------------------------------------------------------------
// Stat (smaller, used in conversion section)
// ---------------------------------------------------------------------------

interface StatProps {
  label: string;
  value: string;
  sub?: string;
  tone?: 'neutral' | 'good' | 'warn';
}

function Stat({ label, value, sub, tone = 'neutral' }: StatProps) {
  return (
    <div
      className={`${styles.stat} ${tone === 'good' ? styles.statGood : tone === 'warn' ? styles.statWarn : ''}`}
    >
      <div className={styles.statLabel}>{label}</div>
      <div className={styles.statValue}>{value}</div>
      {sub && <div className={styles.statSub}>{sub}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Time-series line chart (SVG, no dependency)
// ---------------------------------------------------------------------------

interface TimeSeriesChartProps {
  points: InquiryStatsTimeSeriesPoint[];
  granularity: 'day' | 'week';
}

function TimeSeriesChart({ points, granularity }: TimeSeriesChartProps) {
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);

  const W = 720;
  const H = 220;
  const PAD_L = 36;
  const PAD_R = 12;
  const PAD_T = 16;
  const PAD_B = 28;
  const innerW = W - PAD_L - PAD_R;
  const innerH = H - PAD_T - PAD_B;

  if (points.length === 0) {
    return <div className={styles.chartEmpty}>No data in this window.</div>;
  }

  const maxCount = Math.max(1, ...points.map((p) => p.count));
  // Round max up to next nice number for the y-axis
  const niceMax = (() => {
    if (maxCount <= 5) return 5;
    if (maxCount <= 10) return 10;
    const exp = Math.pow(10, Math.floor(Math.log10(maxCount)));
    return Math.ceil(maxCount / exp) * exp;
  })();

  const xFor = (i: number): number => {
    if (points.length === 1) return PAD_L + innerW / 2;
    return PAD_L + (i / (points.length - 1)) * innerW;
  };
  const yFor = (v: number): number => PAD_T + innerH - (v / niceMax) * innerH;

  const inquiriesPath = points
    .map((p, i) => `${i === 0 ? 'M' : 'L'}${xFor(i).toFixed(2)},${yFor(p.count).toFixed(2)}`)
    .join(' ');

  const inquiriesArea =
    points.length > 1
      ? `${inquiriesPath} L${xFor(points.length - 1).toFixed(2)},${yFor(0).toFixed(2)} L${xFor(0).toFixed(2)},${yFor(0).toFixed(2)} Z`
      : '';

  const wonPath = points
    .map((p, i) => `${i === 0 ? 'M' : 'L'}${xFor(i).toFixed(2)},${yFor(p.won).toFixed(2)}`)
    .join(' ');

  const yTicks = [0, niceMax / 2, niceMax];
  const xTickIndices = (() => {
    const desired = Math.min(6, points.length);
    if (desired <= 1) return [0];
    const idxs = [];
    for (let i = 0; i < desired; i++) {
      idxs.push(Math.round((i / (desired - 1)) * (points.length - 1)));
    }
    return Array.from(new Set(idxs));
  })();

  const fmtX = (date: string): string => {
    try {
      const d = parseISO(date);
      return format(d, granularity === 'week' ? 'MMM d' : 'MMM d');
    } catch {
      return date;
    }
  };

  const handleMove = (e: React.MouseEvent<SVGSVGElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * W;
    const relX = x - PAD_L;
    if (relX < 0 || relX > innerW) {
      setHoverIdx(null);
      return;
    }
    const idx = Math.round((relX / innerW) * (points.length - 1));
    setHoverIdx(Math.max(0, Math.min(points.length - 1, idx)));
  };

  return (
    <div className={styles.chartWrap}>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className={styles.chartSvg}
        preserveAspectRatio="none"
        onMouseMove={handleMove}
        onMouseLeave={() => setHoverIdx(null)}
      >
        {/* Y-axis grid lines + labels */}
        {yTicks.map((t) => (
          <g key={t}>
            <line
              x1={PAD_L}
              x2={W - PAD_R}
              y1={yFor(t)}
              y2={yFor(t)}
              stroke="rgba(255,255,255,0.06)"
              strokeWidth={1}
            />
            <text
              x={PAD_L - 6}
              y={yFor(t) + 4}
              textAnchor="end"
              className={styles.axisLabel}
            >
              {COMPACT_NUM.format(t)}
            </text>
          </g>
        ))}

        {/* X-axis labels */}
        {xTickIndices.map((i) => (
          <text
            key={i}
            x={xFor(i)}
            y={H - 8}
            textAnchor="middle"
            className={styles.axisLabel}
          >
            {fmtX(points[i].date)}
          </text>
        ))}

        {/* Area fill */}
        {inquiriesArea && (
          <path d={inquiriesArea} fill="rgba(59, 130, 246, 0.18)" />
        )}

        {/* Inquiries line */}
        <path
          d={inquiriesPath}
          stroke="#3b82f6"
          strokeWidth={2}
          fill="none"
          strokeLinejoin="round"
          strokeLinecap="round"
        />

        {/* Won line */}
        <path
          d={wonPath}
          stroke="#10b981"
          strokeWidth={2}
          fill="none"
          strokeLinejoin="round"
          strokeLinecap="round"
          strokeDasharray="4 3"
        />

        {/* Hover marker */}
        {hoverIdx != null && (
          <g>
            <line
              x1={xFor(hoverIdx)}
              x2={xFor(hoverIdx)}
              y1={PAD_T}
              y2={H - PAD_B}
              stroke="rgba(255,255,255,0.2)"
              strokeWidth={1}
            />
            <circle
              cx={xFor(hoverIdx)}
              cy={yFor(points[hoverIdx].count)}
              r={4}
              fill="#3b82f6"
              stroke="#0f0f0f"
              strokeWidth={2}
            />
            {points[hoverIdx].won > 0 && (
              <circle
                cx={xFor(hoverIdx)}
                cy={yFor(points[hoverIdx].won)}
                r={4}
                fill="#10b981"
                stroke="#0f0f0f"
                strokeWidth={2}
              />
            )}
          </g>
        )}
      </svg>

      {hoverIdx != null && (() => {
        // Position by percentage; switch the transform anchor near the
        // edges so the tooltip never overflows the chart.
        const pct = (xFor(hoverIdx) / W) * 100;
        const anchor =
          pct < 18
            ? 'translate(0, 0)'
            : pct > 82
            ? 'translate(-100%, 0)'
            : 'translate(-50%, 0)';
        return (
          <div
            className={styles.tooltip}
            style={{ left: `${pct}%`, transform: anchor }}
          >
            <div className={styles.tooltipDate}>{fmtX(points[hoverIdx].date)}</div>
            <div className={styles.tooltipRow}>
              <span style={{ color: '#3b82f6' }}>●</span> Inquiries:{' '}
              <strong>{points[hoverIdx].count}</strong>
            </div>
            <div className={styles.tooltipRow}>
              <span style={{ color: '#10b981' }}>●</span> Won:{' '}
              <strong>{points[hoverIdx].won}</strong>
            </div>
            <div className={styles.tooltipRow}>
              Est. units: <strong>{points[hoverIdx].estimated_units}</strong>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Status donut (SVG)
// ---------------------------------------------------------------------------

interface StatusDonutProps {
  counts: Record<InquiryStatus, number>;
  total: number;
}

function StatusDonut({ counts, total }: StatusDonutProps) {
  const slices = INQUIRY_STATUSES.map((s) => ({
    ...s,
    count: counts[s.key] || 0,
  })).filter((s) => s.count > 0);

  const totalShown = slices.reduce((s, x) => s + x.count, 0);
  const safeTotal = totalShown || 1;

  const SIZE = 180;
  const R_OUTER = 80;
  const R_INNER = 50;
  const CX = SIZE / 2;
  const CY = SIZE / 2;

  const cumulativeCounts = slices.reduce<number[]>(
    (sums, s) => [...sums, (sums[sums.length - 1] ?? 0) + s.count],
    [],
  );

  const arcs = slices.map((s, idx) => {
    const startCount = idx === 0 ? 0 : cumulativeCounts[idx - 1];
    const endCount = cumulativeCounts[idx];
    const startAngle = (startCount / safeTotal) * Math.PI * 2;
    const endAngle = (endCount / safeTotal) * Math.PI * 2;
    const path = describeArc(CX, CY, R_OUTER, R_INNER, startAngle, endAngle);
    return { ...s, path };
  });

  return (
    <div className={styles.donutWrap}>
      <svg viewBox={`0 0 ${SIZE} ${SIZE}`} className={styles.donutSvg}>
        {arcs.length === 0 ? (
          <circle
            cx={CX}
            cy={CY}
            r={(R_OUTER + R_INNER) / 2}
            fill="none"
            stroke="rgba(255,255,255,0.06)"
            strokeWidth={R_OUTER - R_INNER}
          />
        ) : (
          arcs.map((a) => (
            <path key={a.key} d={a.path} fill={a.color} stroke="#0f0f0f" strokeWidth={1} />
          ))
        )}
        <text
          x={CX}
          y={CY - 4}
          textAnchor="middle"
          className={styles.donutCenterValue}
        >
          {total.toLocaleString()}
        </text>
        <text
          x={CX}
          y={CY + 14}
          textAnchor="middle"
          className={styles.donutCenterLabel}
        >
          total
        </text>
      </svg>
      <ul className={styles.donutLegend}>
        {INQUIRY_STATUSES.map((s) => {
          const count = counts[s.key] || 0;
          const pct = total > 0 ? (count / total) * 100 : 0;
          return (
            <li key={s.key} className={styles.donutLegendItem}>
              <span className={styles.donutDot} style={{ background: s.color }} />
              <span className={styles.donutLegendLabel}>
                {INQUIRY_STATUS_MAP[s.key].label}
              </span>
              <span className={styles.donutLegendCount}>{count.toLocaleString()}</span>
              <span className={styles.donutLegendPct}>{pct.toFixed(1)}%</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// SVG donut wedge math
function describeArc(
  cx: number,
  cy: number,
  rOuter: number,
  rInner: number,
  start: number,
  end: number,
): string {
  // Avoid full-circle SVG arc bug — collapse near-full slice to a near-full arc
  const angleSpan = end - start;
  const isFullCircle = angleSpan >= Math.PI * 2 - 1e-6;
  const adjustedEnd = isFullCircle ? end - 1e-3 : end;
  const largeArc = adjustedEnd - start > Math.PI ? 1 : 0;

  const x1 = cx + rOuter * Math.cos(start - Math.PI / 2);
  const y1 = cy + rOuter * Math.sin(start - Math.PI / 2);
  const x2 = cx + rOuter * Math.cos(adjustedEnd - Math.PI / 2);
  const y2 = cy + rOuter * Math.sin(adjustedEnd - Math.PI / 2);

  const x3 = cx + rInner * Math.cos(adjustedEnd - Math.PI / 2);
  const y3 = cy + rInner * Math.sin(adjustedEnd - Math.PI / 2);
  const x4 = cx + rInner * Math.cos(start - Math.PI / 2);
  const y4 = cy + rInner * Math.sin(start - Math.PI / 2);

  return [
    `M ${x1} ${y1}`,
    `A ${rOuter} ${rOuter} 0 ${largeArc} 1 ${x2} ${y2}`,
    `L ${x3} ${y3}`,
    `A ${rInner} ${rInner} 0 ${largeArc} 0 ${x4} ${y4}`,
    'Z',
  ].join(' ');
}

// ---------------------------------------------------------------------------
// Horizontal bar breakdown
// ---------------------------------------------------------------------------

interface BarBreakdownProps {
  title: string;
  data: { value: string; count: number }[];
  total: number;
  barColor: string;
}

function BarBreakdown({ title, data, total, barColor }: BarBreakdownProps) {
  return (
    <section className={styles.chartCard}>
      <div className={styles.chartHeader}>
        <h2 className={styles.chartTitle}>{title}</h2>
      </div>
      {data.length === 0 ? (
        <div className={styles.chartEmpty}>No values yet.</div>
      ) : (
        <ul className={styles.barList}>
          {data.map((d) => {
            const pct = total > 0 ? (d.count / total) * 100 : 0;
            return (
              <li key={d.value} className={styles.barItem}>
                <span className={styles.barLabel} title={d.value}>
                  {d.value || '—'}
                </span>
                <span className={styles.barCount}>{d.count.toLocaleString()}</span>
                <div className={styles.barTrack}>
                  <div
                    className={styles.barFill}
                    style={{ width: `${Math.min(pct, 100)}%`, background: barColor }}
                  />
                </div>
                <span className={styles.barPct}>{pct.toFixed(1)}%</span>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Legend dot
// ---------------------------------------------------------------------------

function LegendDot({ color, label }: { color: string; label: string }) {
  return (
    <span className={styles.legendItem}>
      <span className={styles.legendDot} style={{ background: color }} />
      {label}
    </span>
  );
}
